export type CropSeasonStatus = 'active' | 'upcoming' | 'past';

export interface DatedCropSeason {
  startDateEpoch?: number;
  endDateEpoch?: number;
}

/**
 * Classifies a crop season from its date range.
 *
 * This is the single definition of active/upcoming/past for the crop season
 * breadcrumb. The menu sections seasons with it and the selection state reads
 * it, and the two must agree: a season the menu draws under Past or Upcoming
 * (a radio) while the selection still counts it as active renders as selected
 * beside the genuinely active checkbox. The server's own `active` flag is
 * deliberately not consulted — it can disagree with the dates the menu lays
 * out, which is precisely what produced that double selection.
 *
 * @param season - Crop season carrying its start/end epoch timestamps
 * @param now - Reference timestamp, defaulting to the current time
 * @returns Whether the season is active, still upcoming, or already past
 */
const getSeasonStatus = (
  season: DatedCropSeason,
  now: number = Date.now()
): CropSeasonStatus => {
  // Number() rather than a `?? 0` default: a missing epoch has to compare as
  // NaN, the way the two inline rules this replaces did, so a record with no
  // dates still falls through to 'upcoming' instead of being reported as long
  // past.
  if (now > Number(season?.startDateEpoch)) {
    return now > Number(season?.endDateEpoch) ? 'past' : 'active';
  }
  return 'upcoming';
};

export default getSeasonStatus;
